import type { MapLineData, MapMovementData } from "./api";

export type LatLngTuple = [number, number];
export type GeoCoordinate = [number, number];
export type MovementKind = "straight" | "turn" | "uturn";

type PlanePoint = { x: number; y: number };

const arcSegments = 18;
const epsilon = 1e-10;

export function movementKey(entryLineID: string, exitLineID: string) {
  return `${entryLineID}:${exitLineID}`;
}

export function linePoint(line: MapLineData, fraction: number): LatLngTuple {
  const coordinates = line.geometry.coordinates;
  const first = coordinates[0];
  if (coordinates.length === 1) return [first[1], first[0]];

  const referenceLatitude = coordinates.reduce((sum, coordinate) => sum + coordinate[1], 0) / coordinates.length;
  const longitudeScale = Math.cos(referenceLatitude * Math.PI / 180);
  const segmentLengths = coordinates.slice(1).map((coordinate, index) => {
    const previous = coordinates[index];
    return Math.hypot((coordinate[0] - previous[0]) * longitudeScale, coordinate[1] - previous[1]);
  });
  const totalLength = segmentLengths.reduce((sum, length) => sum + length, 0);
  if (totalLength === 0) return [first[1], first[0]];

  let remaining = Math.min(1, Math.max(0, fraction)) * totalLength;
  for (let index = 0; index < segmentLengths.length; index += 1) {
    const segmentLength = segmentLengths[index];
    if (remaining <= segmentLength || index === segmentLengths.length - 1) {
      const progress = segmentLength === 0 ? 0 : remaining / segmentLength;
      const start = coordinates[index];
      const end = coordinates[index + 1];
      return [
        start[1] + (end[1] - start[1]) * progress,
        start[0] + (end[0] - start[0]) * progress,
      ];
    }
    remaining -= segmentLength;
  }
  const last = coordinates.at(-1) ?? first;
  return [last[1], last[0]];
}

export function lineMidpoint(line: MapLineData): LatLngTuple {
  return linePoint(line, 0.5);
}

export function rightLaneAnchor(line: MapLineData, movementFrom: LatLngTuple, movementTo: LatLngTuple): LatLngTuple {
  const midpoint = lineMidpoint(line);
  const candidates = [linePoint(line, 0.25), linePoint(line, 0.75)];
  const referenceLatitude = (movementFrom[0] + movementTo[0]) / 2;
  const longitudeScale = Math.cos(referenceLatitude * Math.PI / 180);
  const movementEast = (movementTo[1] - movementFrom[1]) * longitudeScale;
  const movementNorth = movementTo[0] - movementFrom[0];
  const rightEast = movementNorth;
  const rightNorth = -movementEast;

  return candidates.reduce((rightmost, candidate) => {
    const score = (candidate[1] - midpoint[1]) * longitudeScale * rightEast
      + (candidate[0] - midpoint[0]) * rightNorth;
    const rightmostScore = (rightmost[1] - midpoint[1]) * longitudeScale * rightEast
      + (rightmost[0] - midpoint[0]) * rightNorth;
    return score > rightmostScore ? candidate : rightmost;
  });
}

function project(origin: LatLngTuple, point: LatLngTuple): PlanePoint {
  const longitudeScale = Math.cos(origin[0] * Math.PI / 180);
  return { x: (point[1] - origin[1]) * longitudeScale, y: point[0] - origin[0] };
}

function unproject(origin: LatLngTuple, point: PlanePoint): LatLngTuple {
  const longitudeScale = Math.cos(origin[0] * Math.PI / 180);
  return [origin[0] + point.y, origin[1] + point.x / longitudeScale];
}

function subtract(first: PlanePoint, second: PlanePoint): PlanePoint {
  return { x: first.x - second.x, y: first.y - second.y };
}

function length(vector: PlanePoint) {
  return Math.hypot(vector.x, vector.y);
}

function normalize(vector: PlanePoint): PlanePoint | null {
  const magnitude = length(vector);
  return magnitude <= epsilon ? null : { x: vector.x / magnitude, y: vector.y / magnitude };
}

function sampleArc(origin: LatLngTuple, center: PlanePoint, radius: number, startAngle: number, sweep: number) {
  return Array.from({ length: arcSegments + 1 }, (_, index) => {
    const angle = startAngle + sweep * index / arcSegments;
    return unproject(origin, {
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    });
  });
}

function appendDistinct(path: LatLngTuple[], point: LatLngTuple) {
  const previous = path.at(-1);
  if (!previous || Math.hypot(previous[0] - point[0], previous[1] - point[1]) > epsilon) path.push(point);
}

function circularTurnPath(origin: LatLngTuple, start: LatLngTuple, end: LatLngTuple, entryMidpoint: LatLngTuple, exitMidpoint: LatLngTuple): LatLngTuple[] {
  const planeStart = project(origin, start);
  const planeEnd = project(origin, end);
  const incoming = normalize(subtract({ x: 0, y: 0 }, project(origin, entryMidpoint)));
  const outgoing = normalize(project(origin, exitMidpoint));
  if (!incoming || !outgoing) return [start, end];

  const dot = Math.max(-1, Math.min(1, incoming.x * outgoing.x + incoming.y * outgoing.y));
  const turnAngle = Math.acos(dot);
  const directionCross = incoming.x * outgoing.y - incoming.y * outgoing.x;
  const turnDirection = Math.sign(directionCross);
  if (turnDirection === 0 || turnAngle <= epsilon || Math.PI - turnAngle <= epsilon) return [start, end];

  // Intersect the two offset right-lane axes. Using the gate center as the
  // corner would let the lateral lane offset distort a 90-degree junction
  // into an elliptical-looking, non-right-angle turn.
  const startToEnd = subtract(planeEnd, planeStart);
  const entryDistance = (startToEnd.x * outgoing.y - startToEnd.y * outgoing.x) / directionCross;
  const laneIntersection = {
    x: planeStart.x + incoming.x * entryDistance,
    y: planeStart.y + incoming.y * entryDistance,
  };
  const exitDistance = (planeEnd.x - laneIntersection.x) * outgoing.x + (planeEnd.y - laneIntersection.y) * outgoing.y;
  if (entryDistance <= epsilon || exitDistance <= epsilon) return [start, end];

  // Use the shorter gate distance as the tangent length. With equally distant,
  // perpendicular gates this is an exact quarter circle; an unequal longer side
  // simply keeps a straight approach or departure before the circular fillet.
  const tangentLength = Math.min(entryDistance, exitDistance);
  const radius = tangentLength / Math.tan(turnAngle / 2);
  if (!Number.isFinite(radius) || radius <= epsilon) return [start, end];

  const firstTangent = {
    x: laneIntersection.x - incoming.x * tangentLength,
    y: laneIntersection.y - incoming.y * tangentLength,
  };
  const secondTangent = {
    x: laneIntersection.x + outgoing.x * tangentLength,
    y: laneIntersection.y + outgoing.y * tangentLength,
  };
  const leftNormal = { x: -incoming.y, y: incoming.x };
  const arcCenter = {
    x: firstTangent.x + leftNormal.x * radius * turnDirection,
    y: firstTangent.y + leftNormal.y * radius * turnDirection,
  };
  const startAngle = Math.atan2(firstTangent.y - arcCenter.y, firstTangent.x - arcCenter.x);
  let endAngle = Math.atan2(secondTangent.y - arcCenter.y, secondTangent.x - arcCenter.x);
  if (turnDirection > 0) {
    while (endAngle <= startAngle) endAngle += Math.PI * 2;
  } else {
    while (endAngle >= startAngle) endAngle -= Math.PI * 2;
  }

  const path: LatLngTuple[] = [];
  appendDistinct(path, start);
  sampleArc(origin, arcCenter, radius, startAngle, endAngle - startAngle).forEach((point) => appendDistinct(path, point));
  appendDistinct(path, end);
  return path;
}

function uturnPath(origin: LatLngTuple, start: LatLngTuple, end: LatLngTuple): LatLngTuple[] {
  const planeStart = project(origin, start);
  const planeEnd = project(origin, end);
  const arcCenter = {
    x: (planeStart.x + planeEnd.x) / 2,
    y: (planeStart.y + planeEnd.y) / 2,
  };
  const radius = length(subtract(planeStart, arcCenter));
  const inward = normalize({ x: -arcCenter.x, y: -arcCenter.y });
  if (!inward || radius <= epsilon) return [start, end];

  const startAngle = Math.atan2(planeStart.y - arcCenter.y, planeStart.x - arcCenter.x);
  const counterClockwiseMidpoint = {
    x: Math.cos(startAngle + Math.PI / 2),
    y: Math.sin(startAngle + Math.PI / 2),
  };
  const clockwiseMidpoint = {
    x: Math.cos(startAngle - Math.PI / 2),
    y: Math.sin(startAngle - Math.PI / 2),
  };
  const counterClockwiseScore = counterClockwiseMidpoint.x * inward.x + counterClockwiseMidpoint.y * inward.y;
  const clockwiseScore = clockwiseMidpoint.x * inward.x + clockwiseMidpoint.y * inward.y;
  return sampleArc(origin, arcCenter, radius, startAngle, counterClockwiseScore >= clockwiseScore ? Math.PI : -Math.PI);
}

export function movementKind(center: LatLngTuple, entryLine: MapLineData, exitLine: MapLineData): MovementKind {
  if (entryLine.id === exitLine.id) return "uturn";
  const entry = lineMidpoint(entryLine);
  const exit = lineMidpoint(exitLine);
  const longitudeScale = Math.cos(center[0] * Math.PI / 180);
  const incoming = [(center[1] - entry[1]) * longitudeScale, center[0] - entry[0]];
  const outgoing = [(exit[1] - center[1]) * longitudeScale, exit[0] - center[0]];
  const divisor = Math.hypot(...incoming) * Math.hypot(...outgoing);
  if (divisor === 0) return "turn";
  const cosine = Math.max(-1, Math.min(1, (incoming[0] * outgoing[0] + incoming[1] * outgoing[1]) / divisor));
  return Math.acos(cosine) * 180 / Math.PI <= 30 ? "straight" : "turn";
}

export function movementPath(center: LatLngTuple, lines: MapLineData[], movement: MapMovementData): LatLngTuple[] {
  if (movement.mode === "disabled") return [];
  const entryLine = lines.find((line) => line.id === movement.entry_line_id);
  const exitLine = lines.find((line) => line.id === movement.exit_line_id);
  if (!entryLine || !exitLine) return [];
  const entryMidpoint = lineMidpoint(entryLine);
  const exitMidpoint = lineMidpoint(exitLine);
  const start = rightLaneAnchor(entryLine, entryMidpoint, center);
  const end = rightLaneAnchor(exitLine, center, exitMidpoint);
  if (movement.mode === "manual" && movement.waypoints.length > 0) {
    return [start, ...movement.waypoints.map(([longitude, latitude]) => [latitude, longitude] as LatLngTuple), end];
  }
  const kind = movementKind(center, entryLine, exitLine);
  if (kind === "straight") return [start, end];
  return kind === "uturn" ? uturnPath(center, start, end) : circularTurnPath(center, start, end, entryMidpoint, exitMidpoint);
}

export function reconcileMovements(lines: MapLineData[], movements: MapMovementData[] = []): MapMovementData[] {
  const existing = new Map(movements.map((movement) => [movementKey(movement.entry_line_id, movement.exit_line_id), movement]));
  return lines.flatMap((entryLine) => lines.map((exitLine) => {
    const previous = existing.get(movementKey(entryLine.id, exitLine.id));
    return previous
      ? { ...previous, waypoints: previous.waypoints.map((point) => [...point] as GeoCoordinate) }
      : { entry_line_id: entryLine.id, exit_line_id: exitLine.id, mode: "auto" as const, waypoints: [] };
  }));
}
